import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import StagePipeline from '../components/StagePipeline';
import TransitionModal from '../components/TransitionModal';
import SubtaskList from '../components/SubtaskList';
import { formatMoney } from '../../utils/money';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { useIsMobile, StickyAction, BottomSheet, Icon, Dialog } from '../../mobile';

const PRIORITY_COLORS = { urgent: '#ef4444', high: '#f59e0b', normal: '#3b82f6', low: '#6b7280' };
const SOURCE_LABELS = { expense: 'Expense', refund_request: 'Refund', new_account: 'New Account' };
const SOURCE_COLORS = { expense: '#f59e0b', refund_request: '#8b5cf6', new_account: '#3b82f6' };

// Work-type labels per vendor-style bucket — mirrors RequestDetailPage so the
// Task Board source panel shows the same fields the submitter was required to
// fill in. The tow-style buckets (refund/no_charge/take_back/discount/bad_tow)
// fall through to the default request render below.
const WORK_LABELS_BY_BUCKET = {
  striping_bid: { striping: 'Striping', pressure_washing: 'Pressure Washing', both: 'Both (Striping + Pressure Washing)' },
  valet_trash: { valet: 'Valet Trash', bulk: 'Bulk Trash', both: 'Both (Valet + Bulk)' },
};
const CUSTOM_FORM_BUCKETS = new Set(Object.keys(WORK_LABELS_BY_BUCKET));

export default function WorkflowItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can, isAtLeastManager, isSuperAdmin } = useAuth();
  const isMobile = useIsMobile();
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [selectedTransition, setSelectedTransition] = useState(null);
  // A move into a step that Hold sub-tasks are still blocking shows why
  // instead of opening the form and failing on submit.
  const [blockedNotice, setBlockedNotice] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  const [uploading, setUploading] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [remindNote, setRemindNote] = useState('');

  const loadItem = useCallback(async () => {
    try {
      const data = await api.getWorkflowItem(id);
      setItem(data);
    } catch (err) {
      console.error('Failed to load item:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadItem(); }, [loadItem]);

  const handleTransition = async ({ field_values, notes }) => {
    setTransitioning(true);
    try {
      const updated = await api.transitionWorkflowItem(id, {
        to_stage_id: selectedTransition.to_stage_id,
        field_values,
        notes,
      });
      setItem(updated);
      setSelectedTransition(null);
    } catch (err) {
      alert(err.message || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setCommenting(true);
    try {
      await api.addWorkflowComment(id, newComment.trim());
      setNewComment('');
      await loadItem();
    } catch (err) {
      alert(err.message || 'Failed to add comment');
    } finally {
      setCommenting(false);
    }
  };

  const handleAddSubtask = async (data) => {
    try {
      await api.addWorkflowSubtask(id, data);
      await loadItem();
    } catch (err) {
      alert(err.message || 'Failed to add subtask');
    }
  };

  const handleToggleSubtask = async (subtaskId) => {
    try {
      await api.toggleWorkflowSubtask(subtaskId);
      await loadItem();
    } catch (err) {
      alert(err.message || 'Failed to toggle subtask');
    }
  };

  const handleUploadFiles = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (const file of files) formData.append('files', file);
      await api.uploadWorkflowAttachments(id, formData);
      await loadItem();
    } catch (err) {
      alert(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    try {
      await api.deleteWorkflowAttachment(attachmentId);
      await loadItem();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>;
  if (!item) return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Item not found</div>;

  const isCompleted = !!item.completed_at;
  // Only owners, supervisors, and admins can trigger transitions
  const isOwner = item.currentOwners?.some(o => o.user_id === user?.id);
  const isSupervisor = item.supervisors?.some(s => s.user_id === user?.id);
  const canTransition = isOwner || isSupervisor || can('manage_tasks') || can('manage_admin');
  // Reassign is manager-level only: it puts a task in the right hands, so
  // it belongs to people with authority over people. Owning the task does
  // NOT grant it. Mirrors the server gate on POST /:id/reassign.
  const canReassign = !isCompleted && (isAtLeastManager || can('manage_admin') || can('reassign_items'));
  // Submission-field editing: owner, supervisor, or admin. Only meaningful
  // for request.* source types today.
  const isRequestSource = typeof item.source_type === 'string' && item.source_type.startsWith('request.');
  // Gate Access has nothing to edit — the card's steps are the whole flow.
  const canEditSource = isRequestSource && item.source_type !== 'request.gate_access'
    && (isOwner || isSupervisor || can('manage_admin'));
  // Outsourcing is configured per bucket by the super admin (Auto-Outsource
  // on the bucket page), never per task. The only per-task control is
  // cancelling an outsourced item, and that is super admin too.
  const canCancelOutsource = !isCompleted && isSuperAdmin;
  // Remind: super admin only (Matt, Sep 21) — pings the current owners.
  const canRemind = !isCompleted && isSuperAdmin
    && item.currentOwners?.some(o => o.user_id !== user?.id);
  const handleRemind = async () => {
    const names = item.currentOwners.filter(o => o.user_id !== user?.id).map(o => o.full_name).join(', ');
    if (!window.confirm(`Send a reminder to ${names}?`)) return;
    setReminding(true);
    try {
      const r = await api.remindWorkflowItem(item.id);
      const parts = [];
      if (r.pushed?.length) parts.push(`Push sent to ${r.pushed.join(', ')}`);
      if (r.texted?.length) parts.push(`Texted ${r.texted.join(', ')} (no push on their device)`);
      setRemindNote(parts.join(' · ') || 'Reminder sent');
      await loadItem();
    } catch (e) {
      setRemindNote(e.message || 'Reminder failed');
    } finally {
      setReminding(false);
    }
  };

  // Phone: the next step is the one primary action, pinned above the tab
  // bar. A denial-type transition never gets the gold button.
  const transitions = (!isCompleted && canTransition && item.transitions) || [];
  const isDenial = t => t.to_stage_type === 'terminal' && /denied|rejected/i.test(t.to_stage_name || '');
  const primaryTransition = transitions.find(t => !isDenial(t)) || transitions[0] || null;
  const otherTransitions = transitions.filter(t => t !== primaryTransition);
  const isBlocked = t => (t?.blocked_by?.length || 0) > 0;
  const tryTransition = (t) => {
    if (isBlocked(t)) { setBlockedNotice(t); return; }
    setBlockedNotice(null);
    setSelectedTransition(t);
  };

  return (
    <div style={{ padding: isMobile ? 0 : '24px 32px', maxWidth: 900 }}>
      {/* Back button */}
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: isMobile ? 15 : 13, marginBottom: 12, padding: isMobile ? '6px 0' : 0, display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: isMobile ? 44 : undefined }}>
        {isMobile ? <><Icon name="back" size={20} /> Back</> : '← Back'}
      </button>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
          {item.source_type && SOURCE_LABELS[item.source_type] && (
            <span style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700,
              backgroundColor: SOURCE_COLORS[item.source_type] + '18',
              color: SOURCE_COLORS[item.source_type],
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              {SOURCE_LABELS[item.source_type]}
            </span>
          )}
          <span style={{
            padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
            backgroundColor: PRIORITY_COLORS[item.priority] + '20',
            color: PRIORITY_COLORS[item.priority],
          }}>
            {item.priority?.toUpperCase()}
          </span>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>#{item.id}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{item.workflow_name}</span>
          <button type="button" onClick={() => navigate(`/workflow/tree/${item.id}`)}
            title={item.parent_item_id ? 'This is a sub-task — see the whole submission' : 'See this submission and all of its sub-tasks'}
            style={{
              marginLeft: 'auto', fontSize: 12, fontWeight: 600, padding: isMobile ? '8px 12px' : '4px 10px',
              borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer',
              minHeight: isMobile ? 40 : undefined,
            }}>
            {item.parent_item_id ? 'Part of a bigger submission · View tree' : 'View tree'}
          </button>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.3 }}>
          {item.title}
        </h1>
        {item.description && (
          <p style={{ color: '#6b7280', fontSize: 14, margin: '6px 0 0', lineHeight: 1.5 }}>{item.description}</p>
        )}
      </div>

      {/* Stage Pipeline + Action Buttons */}
      <div style={{ backgroundColor: '#f9fafb', padding: '16px 20px', borderRadius: 10, marginBottom: 16, border: '1px solid #f3f4f6' }}>
        <StagePipeline stages={item.stages} currentStageId={item.current_stage_id} />

        {/* Action Buttons — right below pipeline, only for owners/managers */}
        {!isMobile && !isCompleted && canTransition && item.transitions?.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', alignSelf: 'center' }}>Next:</span>
            {item.transitions.map(t => (
              <button
                key={t.id}
                className="btn"
                onClick={() => tryTransition(t)}
                title={isBlocked(t) ? `On hold until: ${t.blocked_by.map(b => b.label || b.title).join(', ')}` : undefined}
                style={{
                  backgroundColor: t.to_stage_type === 'terminal' && t.to_stage_name?.toLowerCase().includes('denied')
                    ? '#fee2e2' : (t.to_stage_color + '20'),
                  color: t.to_stage_type === 'terminal' && t.to_stage_name?.toLowerCase().includes('denied')
                    ? '#ef4444' : t.to_stage_color,
                  border: `1px solid ${t.to_stage_color}40`,
                  fontWeight: 600,
                  padding: '8px 16px',
                  opacity: isBlocked(t) ? 0.6 : 1,
                }}
              >
                {isBlocked(t) ? '🔒 ' : ''}{t.to_stage_name}
              </button>
            ))}
          </div>
        )}

        {blockedNotice && (
          <div role="status" style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
            background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
            display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
          }}>
            <span style={{ flex: 1, minWidth: 200 }}>
              🔒 <b>{blockedNotice.to_stage_name}</b> is on hold until {blockedNotice.blocked_by.length === 1 ? 'this sub-task finishes' : 'these sub-tasks finish'}:{' '}
              {blockedNotice.blocked_by.map(b => b.label || b.title).join(', ')}
            </span>
            <button type="button" className="btn" onClick={() => navigate(`/workflow/tree/${item.id}`)}
              style={{ fontSize: 12, padding: '6px 12px', minHeight: isMobile ? 40 : undefined }}>
              View tree
            </button>
            <button type="button" aria-label="Dismiss" onClick={() => setBlockedNotice(null)}
              style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
        )}

        {isCompleted && (
          <div style={{
            marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb',
            color: item.stage_name?.toLowerCase().includes('denied') || item.stage_name?.toLowerCase().includes('rejected')
              ? '#991b1b' : '#166534',
            fontWeight: 600, fontSize: 14,
          }}>
            {item.stage_name} — Completed {item.completed_at ? formatDate(item.completed_at) : ''}
            {item.completed_by_name && ` by ${item.completed_by_name}`}
          </div>
        )}
      </div>

      {/* Quick Comment for submitters who can't transition */}
      {!isCompleted && !canTransition && (
        <form onSubmit={handleAddComment} style={{
          display: 'flex', gap: 8, marginBottom: 16,
          padding: '12px 16px', backgroundColor: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe',
        }}>
          <input
            className="sales-input"
            placeholder="Ask for an update or leave a message..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button type="submit" className="btn" disabled={!newComment.trim() || commenting}
            style={{ backgroundColor: '#3b82f6', color: '#fff', fontWeight: 600, fontSize: 13 }}>
            {commenting ? '...' : 'Send'}
          </button>
        </form>
      )}

      {/* Outsourced status banner */}
      {item.outsourced_email && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: 16, borderRadius: 10,
          background: item.outsource_completed_at ? '#f0fdf4' : '#fffbeb',
          border: `1px solid ${item.outsource_completed_at ? '#bbf7d0' : '#fde68a'}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: item.outsource_completed_at ? '#15803d' : '#b45309' }}>
            {item.outsource_completed_at
              ? `✅ Completed externally by ${item.outsourced_name}`
              : `📤 Outsourced to ${item.outsourced_name} (${item.outsourced_email})`}
          </span>
          {item.outsource_completed_note && (
            <span style={{ fontSize: 13, color: '#334155' }}>— “{item.outsource_completed_note}”</span>
          )}
          {!item.outsource_completed_at && canCancelOutsource && (
            <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button className="btn" onClick={async () => {
                if (!window.confirm('Cancel outsourcing? Their email link will stop working.')) return;
                try { await api.cancelOutsourceTask(item.id); await loadItem(); }
                catch (e) { alert(e.message || 'Cancel failed'); }
              }} style={{
                fontSize: 12, fontWeight: 600, color: '#dc2626', background: '#fff',
                border: '1px solid #fecaca', padding: '4px 10px',
              }}>
                Cancel
              </button>
            </span>
          )}
        </div>
      )}

      {/* Metadata Grid + Reassign */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: isMobile ? 'wrap' : undefined }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, flex: isMobile ? '1 1 100%' : 1 }}>
          <MetaCard label="Submitter" value={item.submitter_name || '—'} />
          <MetaCard label="Current Owners" value={item.currentOwners?.map(o => o.full_name).join(', ') || 'Unassigned'} />
          <MetaCard label="Supervisors" value={item.supervisors?.map(s => s.full_name).join(', ') || 'None'} />
          <MetaCard label="SLA Deadline" value={item.sla_deadline ? formatDateTime(item.sla_deadline) : 'No deadline'}
            highlight={item.sla_deadline && new Date(item.sla_deadline) < new Date() && !item.completed_at} />
        </div>
        {canRemind && (
          <button className="btn" onClick={handleRemind} disabled={reminding} style={{
            backgroundColor: '#fffbeb', color: '#b45309', fontWeight: 600, fontSize: 13,
            border: '1px solid #fcd34d', whiteSpace: 'nowrap', flexShrink: 0, minHeight: isMobile ? 44 : undefined,
          }}>
            {reminding ? 'Sending…' : '🔔 Remind'}
          </button>
        )}
        {canReassign && (
          <button className="btn" onClick={() => setShowReassign(true)} style={{
            backgroundColor: '#eff6ff', color: '#3b82f6', fontWeight: 600, fontSize: 13,
            border: '1px solid #bfdbfe', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            Reassign
          </button>
        )}
        {/* Super-admin: Void / Unvoid for expense-backed workflow items
            (reimbursement requests). Mirrors the ExpenseHistoryPage void
            flow, exposed here because reimbursement rows navigate to this
            page instead of opening the local view modal. */}
        {item.source_type === 'expense' && user?.permission_tier === 'super_admin' && item.source_id && (
          item.source_data?.status === 'voided' ? (
            <button className="btn" style={{
              background: '#0f172a', color: '#fff', fontWeight: 600, fontSize: 13,
              whiteSpace: 'nowrap', flexShrink: 0,
            }} onClick={async () => {
              if (!window.confirm('Restore this voided reimbursement? It will start counting toward KPIs again.')) return;
              try {
                await api.unvoidExpense(item.source_id);
                await loadItem();
              } catch (e) { alert(e.message || 'Unvoid failed'); }
            }}>
              Restore (Unvoid)
            </button>
          ) : (
            <button className="btn" style={{
              background: '#fee2e2', color: '#991b1b', fontWeight: 600, fontSize: 13,
              border: '1px solid #fca5a5', whiteSpace: 'nowrap', flexShrink: 0,
            }} onClick={async () => {
              const reason = window.prompt(
                'Void this reimbursement?\n\n' +
                'Removes it from KPIs, reports, and totals. ' +
                'Reversible from this same page.\n\n' +
                'Optional reason:',
                ''
              );
              if (reason === null) return;
              try {
                await api.voidExpense(item.source_id, reason || null);
                await loadItem();
              } catch (e) { alert(e.message || 'Void failed'); }
            }}>
              Void Reimbursement
            </button>
          )
        )}
      </div>

      {remindNote && (
        <div style={{ marginTop: -8, marginBottom: 16, fontSize: 13, color: '#92400e' }}>{remindNote}</div>
      )}

      {/* Source Context (expense/refund/account details) */}
      {item.source_data && (
        <SourceContextPanel
          sourceType={item.source_type}
          data={item.source_data}
          itemId={item.id}
          canEdit={canEditSource}
          onSaved={loadItem}
        />
      )}

      {/* Tabs: Activity / Attachments / Subtasks / History */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: 16 }}>
        {['activity', 'attachments', 'subtasks', 'history'].map(t => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: activeTab === t ? 700 : 500, fontSize: 13,
              color: activeTab === t ? '#D4A843' : '#6b7280',
              borderBottom: activeTab === t ? '2px solid #D4A843' : '2px solid transparent',
              marginBottom: -2, textTransform: 'capitalize',
            }}
          >
            {t}
            {t === 'subtasks' && item.subtasks?.length > 0 && (
              <span style={{ marginLeft: 4, fontSize: 11, color: '#9ca3af' }}>({item.subtasks.length})</span>
            )}
            {t === 'attachments' && item.attachments?.length > 0 && (
              <span style={{ marginLeft: 4, fontSize: 11, color: '#9ca3af' }}>({item.attachments.length})</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'activity' && (
        <div>
          {/* Comment Form */}
          <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              className="sales-input"
              placeholder="Add a comment..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              style={{ flex: 1 }}
            />
            <button type="submit" className="btn btn-primary" disabled={!newComment.trim() || commenting}
              style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
              {commenting ? '...' : 'Post'}
            </button>
          </form>

          {/* Comments */}
          {item.comments?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {item.comments.map(c => (
                <div key={c.id} style={{ padding: '10px 14px', backgroundColor: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>{c.full_name}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDateTime(c.created_at)}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>{c.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 20 }}>No comments yet</p>
          )}
        </div>
      )}

      {activeTab === 'attachments' && (
        <div>
          {/* Upload button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <label className="btn" style={{
              backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {uploading ? 'Uploading...' : '+ Upload Files'}
              <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={handleUploadFiles} style={{ display: 'none' }} disabled={uploading} />
            </label>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>PDF, images, documents (max 15 MB each)</span>
          </div>

          {/* Attachment list */}
          {item.attachments?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {item.attachments.map(a => {
                const isImage = /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(a.file_name);
                const isPdf = /\.pdf$/i.test(a.file_name);
                return (
                  <div key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    backgroundColor: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6',
                  }}>
                    {/* Thumbnail / icon */}
                    {isImage ? (
                      <img src={a.file_path} alt={a.file_name} style={{
                        width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb',
                      }} />
                    ) : (
                      <div style={{
                        width: 48, height: 48, borderRadius: 6, backgroundColor: isPdf ? '#fef2f2' : '#eff6ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: isPdf ? '#ef4444' : '#3b82f6',
                        border: '1px solid #e5e7eb',
                      }}>
                        {isPdf ? 'PDF' : 'DOC'}
                      </div>
                    )}

                    {/* File info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.file_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                        {a.uploaded_by_name} · {formatDate(a.created_at)}
                        {a.file_size ? ` · ${(a.file_size / 1024).toFixed(0)} KB` : ''}
                      </div>
                    </div>

                    {/* Actions */}
                    <a href={a.file_path} target="_blank" rel="noopener noreferrer"
                      className="btn" style={{ fontSize: 12, padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151', fontWeight: 600, textDecoration: 'none' }}>
                      View
                    </a>
                    <button onClick={() => handleDeleteAttachment(a.id, a.file_name)}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: '4px 8px' }}>
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 20 }}>No attachments yet</p>
          )}
        </div>
      )}

      {activeTab === 'subtasks' && (
        <SubtaskList subtasks={item.subtasks || []} onAdd={handleAddSubtask} onToggle={handleToggleSubtask} />
      )}

      {activeTab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {item.history?.length > 0 ? item.history.map(h => {
            // Edit entries log from_stage_id === to_stage_id. Surface them as
            // "edited details" rather than the default "moved X → X" phrasing.
            const isEdit = h.from_stage_id && h.to_stage_id && h.from_stage_id === h.to_stage_id
              && (h.field_values?.action === 'edit_source_fields' || (h.notes || '').startsWith('[edit]'));
            const editChanges = isEdit && h.field_values?.changes ? h.field_values.changes : null;
            return (
              <div key={h.id} style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: isEdit ? '#f59e0b' : (h.to_stage_color || '#6b7280'), marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#374151' }}>
                    {isEdit ? (
                      <><strong>{h.changed_by_name}</strong> edited details</>
                    ) : h.from_stage_name ? (
                      <><strong>{h.changed_by_name}</strong> moved from <span style={{ color: h.from_stage_color }}>{h.from_stage_name}</span> → <span style={{ color: h.to_stage_color, fontWeight: 600 }}>{h.to_stage_name}</span></>
                    ) : (
                      <><strong>{h.changed_by_name}</strong> created item at <span style={{ color: h.to_stage_color, fontWeight: 600 }}>{h.to_stage_name}</span></>
                    )}
                  </div>
                  {isEdit && editChanges ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      {Object.entries(editChanges).map(([k, v]) => (
                        <div key={k}>
                          <strong>{k}:</strong>{' '}
                          <span style={{ color: '#9ca3af' }}>{formatHistoryValue(v?.from)}</span>{' → '}
                          <span style={{ color: '#111827', fontWeight: 600 }}>{formatHistoryValue(v?.to)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {h.notes && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>{h.notes}</p>}
                      {h.field_values && typeof h.field_values === 'object' && !h.field_values.action && (
                        <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                          {Object.entries(h.field_values).map(([k, v]) => (
                            <div key={k}><strong>{k}:</strong> {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    {formatDateTime(h.changed_at)}
                    {h.duration_seconds ? ` · ${formatDuration(h.duration_seconds)} in previous stage` : ''}
                  </span>
                </div>
              </div>
            );
          }) : (
            <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: 20 }}>No history yet</p>
          )}
        </div>
      )}

      {/* Phone: pinned next step + a sheet for the other transitions */}
      {isMobile && primaryTransition && (
        <StickyAction>
          {otherTransitions.length === 1 && (
            <button type="button" className="m-btn m-btn-secondary" style={{ width: 'auto', padding: '0 16px', height: 52, color: isDenial(otherTransitions[0]) ? '#dc2626' : undefined }}
              onClick={() => tryTransition(otherTransitions[0])}>
              {isBlocked(otherTransitions[0]) ? '🔒 ' : ''}{otherTransitions[0].to_stage_name}
            </button>
          )}
          {otherTransitions.length > 1 && (
            <button type="button" className="m-btn-icon" aria-label="More actions" onClick={() => setShowMoreActions(true)}>
              <Icon name="more" size={22} />
            </button>
          )}
          <button type="button" className="m-btn m-btn-primary" onClick={() => tryTransition(primaryTransition)}>
            {isBlocked(primaryTransition) ? '🔒 ' : ''}{primaryTransition.to_stage_name}
          </button>
        </StickyAction>
      )}
      <BottomSheet open={showMoreActions} title="Move this item to" onClose={() => setShowMoreActions(false)}>
        {transitions.map(t => (
          <button key={t.id} type="button" className="m-choice" onClick={() => { setShowMoreActions(false); tryTransition(t); }}>
            <span className="m-choice-dot" style={{ background: t.to_stage_color, border: 0 }} />
            <span><div className="m-choice-label" style={isDenial(t) ? { color: '#dc2626' } : {}}>{isBlocked(t) ? '🔒 ' : ''}{t.to_stage_name}</div></span>
          </button>
        ))}
      </BottomSheet>

      {/* Transition Modal */}
      {selectedTransition && (
        <TransitionModal
          transition={selectedTransition}
          onConfirm={handleTransition}
          onClose={() => setSelectedTransition(null)}
          loading={transitioning}
        />
      )}

      {showReassign && (
        <ReassignModal
          item={item}
          onClose={() => setShowReassign(false)}
          onReassigned={(updated) => { setItem(updated); setShowReassign(false); }}
        />
      )}
    </div>
  );
}

function MetaCard({ label, value, highlight }) {
  return (
    <div style={{
      padding: '10px 14px', backgroundColor: highlight ? '#fef2f2' : '#f9fafb',
      borderRadius: 8, border: highlight ? '1px solid #fecaca' : '1px solid #f3f4f6',
    }}>
      <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: highlight ? '#ef4444' : '#374151' }}>{value}</div>
    </div>
  );
}

function ReassignModal({ item, onClose, onReassigned }) {
  const [mode, setMode] = useState('user'); // 'user' | 'team' | 'escalate'
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.getActiveUsers(), api.getTeams()])
      .then(([u, t]) => { setUsers(u); setTeams(t); })
      .catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = { mode, notes: notes.trim() || null };
      if (mode === 'user') payload.user_id = Number(selectedUserId);
      if (mode === 'team') payload.team_id = Number(selectedTeamId);
      const updated = await api.reassignWorkflowItem(item.id, payload);
      onReassigned(updated);
    } catch (err) {
      setError(err.message || 'Reassignment failed');
    }
    setSubmitting(false);
  }

  const canSubmit = mode === 'escalate' || (mode === 'user' && selectedUserId) || (mode === 'team' && selectedTeamId);

  return (
    <Dialog onClose={onClose} busy={submitting} maxWidth={440}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Reassign Item</h2>
        {error && <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {/* Mode Selector */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[
            { key: 'user', label: 'Assign to User' },
            { key: 'team', label: 'Transfer to Team' },
            { key: 'escalate', label: 'Escalate to CEO' },
          ].map(m => (
            <button key={m.key} type="button" onClick={() => setMode(m.key)} style={{
              flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: mode === m.key ? '2px solid #3b82f6' : '2px solid #e5e7eb',
              backgroundColor: mode === m.key ? '#eff6ff' : '#fff',
              color: mode === m.key ? '#3b82f6' : '#6b7280',
            }}>
              {m.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {mode === 'user' && (
            <div style={{ marginBottom: 12 }}>
              <label className="sales-label">Assign To</label>
              <select className="sales-input" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} style={{ width: '100%' }} required>
                <option value="">Select a user...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
          )}
          {mode === 'team' && (
            <div style={{ marginBottom: 12 }}>
              <label className="sales-label">Transfer To Team</label>
              <select className="sales-input" value={selectedTeamId} onChange={e => setSelectedTeamId(e.target.value)} style={{ width: '100%' }} required>
                <option value="">Select a team...</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Will assign to team supervisor(s)</p>
            </div>
          )}
          {mode === 'escalate' && (
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              This will reassign the item to the CEO / super admin for executive review.
            </p>
          )}
          <div style={{ marginBottom: 16 }}>
            <label className="sales-label">Notes (optional)</label>
            <textarea className="sales-input" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Reason for reassignment..." rows={2} style={{ width: '100%', resize: 'vertical' }} />
          </div>
          <div className="m-dialog-actions">
            <button type="button" className="btn" onClick={onClose} style={{ color: '#6b7280' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !canSubmit}
              style={{ backgroundColor: '#3b82f6', color: '#fff', fontWeight: 600 }}>
              {submitting ? 'Reassigning...' : 'Reassign'}
            </button>
          </div>
        </form>
    </Dialog>
  );
}

function SourceContextPanel({ sourceType, data, itemId, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);

  if (!data) return null;

  // All Sales Requests buckets (refund, take_back, discount, no_charge,
  // bad_tow) use source_type='request.{bucket}'. Render everything the
  // submitter actually entered — the workflow owner shouldn't have to click
  // through to see the $ amount or the tow date.
  const isRequest = typeof sourceType === 'string' && sourceType.startsWith('request.');
  // Sign + Tagging share this whole render path and both are closed from the
  // field, so both get the "edit on the bucket page" link instead of the
  // inline Task Board editor.
  const FIELD_JOB_TYPES = { sign_request: 'Sign Request', tagging_request: 'Tagging Request' };
  const fieldJobLabel = isRequest ? FIELD_JOB_TYPES[data.type] : null;
  const isSignRequest = !!fieldJobLabel;
  const editMeta = data.edit_meta || { has_amount: false, has_emergency: false, amount_options: [], reasons: [] };

  const beginEdit = () => {
    setForm({
      vehicle_info: data.vehicle_info || '',
      tow_date: data.tow_date || '',
      reason_id: data.reason_id || '',
      amount_requested: data.amount_requested ?? '',
      refund_category: data.refund_category || '',
      is_emergency: !!data.is_emergency,
      bad_tow: !!data.bad_tow,
      bad_tow_reason: data.bad_tow_reason || '',
      notes: data.notes || '',
      // Refund-specific — harmless to track on other buckets, the server
      // rejects writes unless type='refund'.
      payer_name: data.payer_name || '',
      payer_phone: data.payer_phone || '',
      payer_email: data.payer_email || '',
      original_payment_method: data.original_payment_method || '',
      release_doc_info: data.release_doc_info || '',
      check_mailing_address: data.check_mailing_address || '',
      gm_call_number: data.gm_call_number || '',
      gm_tow_date: data.gm_tow_date || '',
      gm_transaction_name: data.gm_transaction_name || '',
      gm_approved_amount: data.gm_approved_amount ?? '',
    });
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(null);
    setError('');
  };

  const saveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        vehicle_info: form.vehicle_info,
        tow_date: form.tow_date || null,
        notes: form.notes,
        bad_tow: form.bad_tow ? 1 : 0,
        bad_tow_reason: form.bad_tow ? form.bad_tow_reason : null,
      };
      if (form.reason_id !== '' && form.reason_id !== null) payload.reason_id = Number(form.reason_id);
      if (editMeta.has_amount) {
        payload.refund_category = form.refund_category || null;
        payload.amount_requested = form.refund_category === 'other'
          ? (form.amount_requested === '' ? null : Number(form.amount_requested))
          : null;
      }
      if (editMeta.has_emergency) payload.is_emergency = form.is_emergency ? 1 : 0;

      // Refund-only fields. Send them regardless of bucket — the server
      // rejects them with a clear 400 on non-refund requests.
      if (data.type === 'refund') {
        payload.payer_name             = form.payer_name;
        payload.payer_phone            = form.payer_phone;
        payload.payer_email            = form.payer_email;
        payload.original_payment_method = form.original_payment_method;
        payload.release_doc_info       = form.release_doc_info;
        payload.check_mailing_address  = form.check_mailing_address;
        payload.gm_call_number         = form.gm_call_number;
        payload.gm_tow_date            = form.gm_tow_date;
        payload.gm_transaction_name    = form.gm_transaction_name;
        payload.gm_approved_amount     = form.gm_approved_amount === '' ? null : Number(form.gm_approved_amount);
      }

      await api.updateWorkflowItemSource(itemId, payload);
      setEditing(false);
      setForm(null);
      if (typeof onSaved === 'function') await onSaved();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const formatAmount = () => {
    const cat = data.refund_category_label || data.refund_category;
    if (data.amount_requested) {
      const amt = formatMoney(data.amount_requested);
      return cat ? `${amt} (${cat})` : amt;
    }
    return cat || 'Not specified';
  };

  const renderContent = () => {
    if (isRequest) {
      const bucket = data.type;

      // Sign Request — scheduling fields, not tow fields. The schedule
      // status line keeps the card honest about where the job stands:
      // Pending Date Assignment → Date Assigned (with the date) → On
      // Route → Completed.
      if (bucket === 'sign_request' || bucket === 'tagging_request') {
        const fmtWorkDate = (d) => d
          ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
            })
          : null;
        const scheduleStatus = data.status === 'completed'
          ? `✅ Completed${data.scheduled_date ? ` — ${fmtWorkDate(data.scheduled_date)}` : ''}`
          : data.status === 'cancelled' || data.status === 'denied'
            ? '🚫 Cancelled'
            : data.status === 'published'
              ? `🚚 On Route — ${fmtWorkDate(data.scheduled_date)}`
              : data.scheduled_date
                ? `📅 Date Assigned — ${fmtWorkDate(data.scheduled_date)}`
                : '⏳ Pending Date Assignment';
        return (
          <>
            <div style={{ gridColumn: '1 / -1', marginBottom: 4 }}>
              <span style={{
                display: 'inline-block', padding: '6px 12px', borderRadius: 8,
                background: '#fff', border: '1px solid #fde68a',
                fontSize: 13, fontWeight: 700, color: '#92400e',
              }}>
                {scheduleStatus}
              </span>
            </div>
            <InfoRow label="Property" value={data.account_name || '—'} />
            <InfoRow label="Region" value={(bucket === 'tagging_request' ? data.tagging_region : data.sign_region) || '—'} />
            <InfoRow label="Work Requested" value={(bucket === 'tagging_request' ? data.tagging_work_summary : data.sign_work_summary) || '—'} />
            <InfoRow
              label="Estimated Time"
              value={data.estimated_minutes
                ? (data.estimated_minutes >= 60
                    ? `${data.estimated_minutes / 60} hour${data.estimated_minutes > 60 ? 's' : ''}`
                    : `${data.estimated_minutes} minutes`)
                : '—'}
            />
            <InfoRow label="Work Address" value={data.work_address || '—'} />
            {data.is_emergency ? <InfoRow label="Emergency" value="🚨 YES" /> : null}
            {data.access_instructions ? (
              <InfoRow label="Access Instructions" value={data.access_instructions} />
            ) : null}
            {data.special_sign_description ? (
              <InfoRow label="Custom / Special Sign" value={data.special_sign_description} />
            ) : null}
            {data.special_tagging_description ? (
              <InfoRow label="Special Tagging Instructions" value={data.special_tagging_description} />
            ) : null}
            <InfoRow label="Submitted by" value={data.submitted_by_name || '—'} />
            {data.notes ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Work Description</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            ) : null}
          </>
        );
      }

      // Vendor buckets (Striping Bid, Valet Trash) capture a different field
      // set than the tow buckets. Render the same fields the Sales detail page
      // shows so the Task Board — where work actually gets processed — is never
      // missing required submission info.
      if (CUSTOM_FORM_BUCKETS.has(bucket)) {
        const workLabel = (WORK_LABELS_BY_BUCKET[bucket] || {})[data.work_request_type] || data.work_request_type;
        const isMapImage = data.map_url && /\.(png|jpe?g|gif|webp)$/i.test(data.map_url);
        return (
          <>
            <InfoRow label="Work Type" value={workLabel || '—'} />
            <InfoRow
              label="Timeline"
              value={data.requested_timeline === 'emergency' ? '🚨 Emergency' : (data.requested_timeline === 'standard' ? 'Standard' : '—')}
            />
            <InfoRow
              label="Property"
              value={
                <div>
                  <div>{data.account_name || '—'}</div>
                  {data.property_address_other ? (
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{data.property_address_other}</div>
                  ) : null}
                </div>
              }
            />
            <InfoRow
              label="Requestor"
              value={
                <div>
                  <div>{data.requestor_name || '—'}</div>
                  {data.requestor_email ? (
                    <a href={`mailto:${data.requestor_email}`} style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}>
                      {data.requestor_email}
                    </a>
                  ) : null}
                </div>
              }
            />
            {data.scope_of_work ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Scope of Work</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.scope_of_work}</div>
              </div>
            ) : null}
            {data.map_url ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>Map</div>
                <a href={data.map_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
                  {isMapImage ? (
                    <img src={data.map_url} alt="Property map"
                      style={{ maxWidth: 320, maxHeight: 220, borderRadius: 8, border: '1px solid #e5e7eb', objectFit: 'contain', background: '#fff' }} />
                  ) : (
                    <span style={{ fontSize: 13, color: '#3b82f6', fontWeight: 600 }}>📎 View map file</span>
                  )}
                </a>
              </div>
            ) : null}
            <InfoRow label="Submitted by" value={data.submitted_by_name || '—'} />
            {data.notes ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Notes</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            ) : null}
          </>
        );
      }

      // Gate Access — driver can't get through a gate. Super admin assigns
      // the rep (Assign Rep → Rep Assigned); the rep picks the access type,
      // then marks Access Granted (updated in ParkingSnap / provided to
      // driver), which texts the driver.
      if (bucket === 'gate_access') {
        return (
          <>
            <InfoRow label="Property" value={data.account_name || '—'} />
            <InfoRow label="Property Address" value={data.property_address || '—'} />
            <InfoRow label="Reported by" value={data.submitted_by_name || '—'} />
            <InfoRow label="Rep" value={data.gate_rep_name || 'Not assigned yet'} />
            {data.gate_access_type ? (
              <InfoRow label="Access type" value={{
                code: 'Gate code', app: 'App', clicker: 'Clicker / remote', toll_tag: 'Toll tag', other: 'Other',
              }[data.gate_access_type] || data.gate_access_type} />
            ) : null}
            {data.gate_resolution ? (
              <InfoRow label="Access granted" value={{
                parkingsnap: 'Updated in ParkingSnap', driver: 'Provided to Driver',
              }[data.gate_resolution] || data.gate_resolution} />
            ) : null}
            {data.notes ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>What's not working</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            ) : null}
          </>
        );
      }

      // Sponsored Event — property / requested date / requestor + event details.
      if (bucket === 'sponsored_event') {
        return (
          <>
            <InfoRow label="Property" value={data.account_name || '—'} />
            <InfoRow
              label="Requested Date"
              value={data.requested_date
                ? new Date(data.requested_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                : '—'}
            />
            <InfoRow label="Requestor" value={data.requestor_name || '—'} />
            {data.scope_of_work ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Event Details</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.scope_of_work}</div>
              </div>
            ) : null}
            <InfoRow label="Submitted by" value={data.submitted_by_name || '—'} />
            {data.notes ? (
              <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Notes</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            ) : null}
          </>
        );
      }

      return (
        <>
          <InfoRow label="Account" value={data.account_name || '—'} />
          <InfoRow label="Property Address" value={data.property_address || '—'} />
          <InfoRow label="Vehicle" value={data.vehicle_info || '—'} />
          <InfoRow label="Tow Date" value={data.tow_date || '—'} />
          <InfoRow label="Reason" value={data.reason_name || data.reason || '—'} />
          <InfoRow label="Amount" value={formatAmount()} />
          {data.is_emergency ? (
            <InfoRow label="Emergency" value="🚨 YES" />
          ) : null}
          {data.bad_tow ? (
            <>
              <InfoRow label="Bad Tow" value="Flagged" />
              <InfoRow label="Bad Tow Reason" value={data.bad_tow_reason || '—'} />
            </>
          ) : null}
          {data.driver_user_name ? (
            <InfoRow label="Driver" value={data.driver_user_name} />
          ) : null}
          {data.deduction_amount ? (
            <InfoRow label="Deduction" value={formatMoney(data.deduction_amount)} />
          ) : null}
          {data.investigation_outcome ? (
            <InfoRow label="Investigation Outcome" value={data.investigation_outcome} />
          ) : null}
          {data.payer_name ? (
            <InfoRow label="Payer" value={data.payer_name} />
          ) : null}
          {data.payer_phone ? (
            <InfoRow label="Payer Phone" value={data.payer_phone} />
          ) : null}
          {data.payer_email ? (
            <InfoRow label="Payer Email" value={data.payer_email} />
          ) : null}
          {data.original_payment_method ? (
            <InfoRow label="Original Payment" value={data.original_payment_method} />
          ) : null}
          {data.release_doc_info ? (
            <InfoRow label="Release Doc" value={data.release_doc_info} />
          ) : null}
          {data.check_mailing_address ? (
            <InfoRow label="Check Mailing Address" value={data.check_mailing_address} />
          ) : null}
          {/* GM-verified transaction fields (populated on the refund's GM
              approval transition). Grouped visually below submission info. */}
          {(data.gm_call_number || data.gm_tow_date || data.gm_transaction_name || data.gm_approved_amount) ? (
            <div style={{ gridColumn: '1 / -1', marginTop: 8, paddingTop: 10, borderTop: '1px dashed #fcd34d' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                GM Approval (looked up transaction)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                {data.gm_call_number ? <InfoRow label="Call Number" value={data.gm_call_number} /> : null}
                {data.gm_tow_date ? <InfoRow label="Date Towed" value={data.gm_tow_date} /> : null}
                {data.gm_transaction_name ? <InfoRow label="Name on Transaction" value={data.gm_transaction_name} /> : null}
                {data.gm_approved_amount !== null && data.gm_approved_amount !== undefined
                  ? <InfoRow label="Approved Amount" value={formatMoney(data.gm_approved_amount)} />
                  : null}
              </div>
            </div>
          ) : null}
          <InfoRow label="Submitted by" value={data.submitted_by_name || '—'} />
          {data.notes ? (
            <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{data.notes}</div>
            </div>
          ) : null}
        </>
      );
    }
    switch (sourceType) {
      case 'expense':
        return (
          <>
            <InfoRow label="Amount" value={data.amount ? formatMoney(data.amount) : '—'} />
            <InfoRow label="Type" value={data.expense_type || '—'} />
            <InfoRow label="Account" value={data.account_names || data.account_name_other || '—'} />
            <InfoRow label="Submitted by" value={data.expense_for_name || data.submitted_by_name || '—'} />
            <InfoRow label="Status" value={data.status || '—'} />
          </>
        );
      case 'refund_request':
        return (
          <>
            <InfoRow label="Account" value={data.account_name || '—'} />
            <InfoRow label="Amount" value={data.amount_requested ? formatMoney(data.amount_requested) : 'TBD'} />
            <InfoRow label="Reason" value={data.reason || '—'} />
            <InfoRow label="Vehicle" value={data.vehicle_info || '—'} />
            <InfoRow label="Submitted by" value={data.submitted_by_name || '—'} />
          </>
        );
      case 'new_account':
        return (
          <>
            <InfoRow label="Property" value={data.property_name || '—'} />
            <InfoRow label="Address" value={data.property_address || '—'} />
            <InfoRow label="Contact" value={data.contact_name || '—'} />
            <InfoRow label="Submitted by" value={data.submitted_by_name || '—'} />
          </>
        );
      default:
        return <p style={{ color: '#9ca3af', fontSize: 13 }}>Source data available</p>;
    }
  };

  const typeLabels = { expense: 'Expense Details', refund_request: 'Refund Details', new_account: 'Account Details' };
  let heading = typeLabels[sourceType] || 'Source Details';
  if (isRequest) {
    heading = `${data.type_label || 'Request'} Details`;
  }

  return (
    <div style={{
      padding: 16, backgroundColor: '#fffbeb', borderRadius: 10,
      border: '1px solid #fde68a', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#92400e', margin: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {heading}
        </h4>
        {isRequest && canEdit && !editing && (isSignRequest ? (
          <a
            href={`/sales/requests/${data.type}/${data.id}`}
            className="btn"
            style={{
              fontSize: 11, padding: '4px 10px', fontWeight: 600, textDecoration: 'none',
              backgroundColor: '#fde68a', color: '#92400e', border: '1px solid #f59e0b', borderRadius: 6,
            }}
          >
            ✏️ Edit on {fieldJobLabel} →
          </a>
        ) : (
          <button
            onClick={beginEdit}
            className="btn"
            style={{
              fontSize: 11, padding: '4px 10px', fontWeight: 600,
              backgroundColor: '#fde68a', color: '#92400e', border: '1px solid #f59e0b', borderRadius: 6,
            }}
          >
            ✏️ Edit
          </button>
        ))}
      </div>

      {editing && form ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <FormField label="Vehicle">
              <input
                className="sales-input"
                value={form.vehicle_info}
                onChange={e => updateField('vehicle_info', e.target.value)}
                style={{ width: '100%', fontSize: 13 }}
              />
            </FormField>
            <FormField label="Tow Date">
              <input
                className="sales-input"
                type="date"
                value={form.tow_date || ''}
                onChange={e => updateField('tow_date', e.target.value)}
                style={{ width: '100%', fontSize: 13 }}
              />
            </FormField>
            {editMeta.reasons.length > 0 && (
              <FormField label="Reason">
                <select
                  className="sales-input"
                  value={form.reason_id || ''}
                  onChange={e => updateField('reason_id', e.target.value)}
                  style={{ width: '100%', fontSize: 13 }}
                >
                  <option value="">—</option>
                  {editMeta.reasons.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </FormField>
            )}
            {editMeta.has_amount && (
              <>
                <FormField label="Amount Category">
                  <select
                    className="sales-input"
                    value={form.refund_category || ''}
                    onChange={e => updateField('refund_category', e.target.value)}
                    style={{ width: '100%', fontSize: 13 }}
                  >
                    <option value="">—</option>
                    {editMeta.amount_options.map(opt => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </FormField>
                {form.refund_category === 'other' && (
                  <FormField label="Amount ($)">
                    <input
                      className="sales-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.amount_requested}
                      onChange={e => updateField('amount_requested', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }}
                    />
                  </FormField>
                )}
              </>
            )}
            {editMeta.has_emergency && (
              <FormField label="Emergency">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={form.is_emergency}
                    onChange={e => updateField('is_emergency', e.target.checked)}
                  />
                  Flag as 🚨 emergency
                </label>
              </FormField>
            )}
            <FormField label="Bad Tow">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.bad_tow}
                  onChange={e => updateField('bad_tow', e.target.checked)}
                />
                Flag as bad tow
              </label>
            </FormField>
            {form.bad_tow && (
              <FormField label="Bad Tow Reason" fullWidth>
                <textarea
                  className="sales-input"
                  rows={2}
                  value={form.bad_tow_reason}
                  onChange={e => updateField('bad_tow_reason', e.target.value)}
                  style={{ width: '100%', fontSize: 13 }}
                />
              </FormField>
            )}
          </div>

          {/* Refund-specific: payer + GM approval blocks */}
          {data.type === 'refund' && (
            <>
              <div style={{
                marginTop: 4, padding: 10, borderRadius: 8,
                background: '#fff', border: '1px solid #fde68a',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
                  Payer Information
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  <FormField label="Payer Name">
                    <input className="sales-input" value={form.payer_name}
                      onChange={e => updateField('payer_name', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Payer Phone">
                    <input className="sales-input" type="tel" value={form.payer_phone}
                      onChange={e => updateField('payer_phone', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Payer Email">
                    <input className="sales-input" type="email" value={form.payer_email}
                      onChange={e => updateField('payer_email', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Original Payment Method">
                    <select className="sales-input" value={form.original_payment_method}
                      onChange={e => updateField('original_payment_method', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }}>
                      <option value="">—</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="debit_card">Debit Card</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </FormField>
                  <FormField label="Release Doc / Receipt #">
                    <input className="sales-input" value={form.release_doc_info}
                      onChange={e => updateField('release_doc_info', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Check Mailing Address" fullWidth>
                    <input className="sales-input" value={form.check_mailing_address}
                      onChange={e => updateField('check_mailing_address', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                </div>
              </div>

              <div style={{
                marginTop: 4, padding: 10, borderRadius: 8,
                background: '#fff', border: '1px solid #fde68a',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
                  GM Approval (looked up from transaction)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  <FormField label="Call Number">
                    <input className="sales-input" value={form.gm_call_number}
                      onChange={e => updateField('gm_call_number', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Date Towed (GM verified)">
                    <input className="sales-input" type="date" value={form.gm_tow_date}
                      onChange={e => updateField('gm_tow_date', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Name on Transaction">
                    <input className="sales-input" value={form.gm_transaction_name}
                      onChange={e => updateField('gm_transaction_name', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                  <FormField label="Approved Amount ($)">
                    <input className="sales-input" type="number" step="0.01" min="0"
                      value={form.gm_approved_amount}
                      onChange={e => updateField('gm_approved_amount', e.target.value)}
                      style={{ width: '100%', fontSize: 13 }} />
                  </FormField>
                </div>
              </div>
            </>
          )}

          <FormField label="Notes" fullWidth>
            <textarea
              className="sales-input"
              rows={3}
              value={form.notes}
              onChange={e => updateField('notes', e.target.value)}
              style={{ width: '100%', fontSize: 13 }}
            />
          </FormField>
          {error && (
            <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="btn"
              style={{ fontSize: 12, padding: '6px 14px', backgroundColor: '#f3f4f6', color: '#6b7280' }}
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="btn"
              style={{ fontSize: 12, padding: '6px 14px', backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
          {renderContent()}
        </div>
      )}
    </div>
  );
}

function FormField({ label, fullWidth, children }) {
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{value}</div>
    </div>
  );
}

// Render a from/to value from an edit-history entry. Nulls/empties get a
// placeholder so the diff reads cleanly. Long strings are truncated.
function formatHistoryValue(v) {
  if (v === null || v === undefined || v === '') return <em style={{ color: '#9ca3af' }}>(empty)</em>;
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 50 ? s.slice(0, 47) + '…' : s;
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}
